import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { registerAppToast } from "@/shared/lib/toastBridge";
import { cn } from "./cn";
import type { Tone } from "./StatusBadge";

const toneAccent: Record<Tone, string> = {
  neutral: "border-l-slate-400",
  info: "border-l-blue-500",
  success: "border-l-green-500",
  warning: "border-l-amber-500",
  danger: "border-l-red-500",
  brand: "border-l-brand-600",
};

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItemProps {
  tone?: Tone;
  title?: ReactNode;
  message: ReactNode;
  action?: ToastAction;
  onClose?: () => void;
}

/** Presentational toast card. */
export function Toast({ tone = "neutral", title, message, action, onClose }: ToastItemProps) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-3 w-80 max-w-[90vw] bg-white rounded-card shadow-lg border border-slate-200 border-l-4 px-4 py-3 motion-safe:animate-scaleIn",
        toneAccent[tone],
      )}
    >
      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-semibold text-ink-900">{title}</p>}
        <p className="text-sm text-ink-600 break-words">{message}</p>
        {action ? (
          <button
            type="button"
            onClick={() => {
              action.onClick();
              onClose?.();
            }}
            className="mt-2 text-sm font-bold text-brand-600 hover:text-brand-700"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="text-ink-400 hover:text-ink-900 shrink-0"
          aria-label="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// ── Toast provider + hook (replaces blocking alert() in app flows) ──────────
interface ToastRecord extends ToastItemProps {
  id: number;
}

interface ToastApi {
  show: (t: ToastItemProps & { durationMs?: number }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastApi["show"]>(
    ({ durationMs = 4000, ...rest }) => {
      const id = Date.now() + Math.random();
      setToasts((list) => [...list, { id, ...rest }]);
      if (durationMs > 0) window.setTimeout(() => remove(id), durationMs);
    },
    [remove],
  );

  const api = useMemo(() => ({ show }), [show]);

  useEffect(() => {
    registerAppToast(show);
    return () => registerAppToast(null);
  }, [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed z-[200] flex flex-col gap-2 items-end fixed-safe-top-right pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <Toast {...t} onClose={() => remove(t.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
}
